"use client"
import { TransitionLink } from '@/components/TransitionLink';
import { useAuth } from '@/context/AuthProvider';
import useChatWebSocket from '@/hooks/useChatWebSocket';
import { MessageCircle, Phone, MoreVertical, UserPlus, UserMinus, Shield, Flag, BadgeCheck } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SellerSectionDesktop = ({ sallerInfo }) => {
  // All state and hooks remain the same
  const { t } = useTranslation();
  const { getToken, user } = useAuth();
  const token = getToken();
  const i18nextLng = typeof window !== 'undefined' ? window.localStorage.getItem("i18nextLng") : 'en';
  const { sendMessage } = useChatWebSocket(token ? token : null, sallerInfo?.username)
  const [state, setState] = useState({
    followLoading: false,
    blockLoading: false,
    isMenuOpen: false,
    rooms: [],
    sallerStatus: {},
    showBlockDialog: false,
    showReportDialog: false,
    reportDescription: '',
    chatModel: false,
    chatMessage: ""
  });

  const router = useRouter();

  // All existing callback functions remain the same
  const fetchSallerInfo = useCallback(async () => {
    if (!token) return;
    try {
      // Use static data instead of API call
      const { staticSellerStatus } = await import('@/data/staticData');
      setState(prev => ({ ...prev, sallerStatus: staticSellerStatus }));
    } catch (error) {
      toast.error(error.message);
    }
  }, [sallerInfo?.username, token]);

  const fetchRooms = useCallback(async () => {
    if (!token) return;
    try {
      // Use static data instead of API call
      const { staticRooms } = await import('@/data/staticData');
      setState(prev => ({ ...prev, rooms: staticRooms || [] }));
    } catch (error) {
      console.error('Fetch rooms error:', error);
    }
  }, [token]);

  const toggleFollowStatus = useCallback(async () => {
    if (!token) {
      toast.error(t("Login Required"));
      return;
    }

    setState(prev => ({ ...prev, followLoading: true }));
    try {
      const endpoint = state.sallerStatus.followed
        ? `${process.env.NEXT_PUBLIC_BASE_URL}/auth/unfollow-account/${sallerInfo?.username}`
        : `${process.env.NEXT_PUBLIC_BASE_URL}/auth/follow-account/${sallerInfo?.username}`;

      // Simulate API call with static data
      await new Promise(resolve => setTimeout(resolve, 300));
      await fetchSallerInfo();

      setState(prev => ({
        ...prev,
        followLoading: false
      }));

      toast.success(state.sallerStatus.followed ? t("sallerInfo.success.unfollowed") : t("sallerInfo.success.followed"));
    } catch (error) {
      toast.error(error.message);
      setState(prev => ({ ...prev, followLoading: false }));
    }
  }, [sallerInfo?.username, token, t, fetchSallerInfo, state.sallerStatus.followed]);

  const toggleBlockStatus = useCallback(async () => {
    if (!token) {
      toast.error(t("Login Required"));
      return;
    }

    setState(prev => ({ ...prev, blockLoading: true }));
    try {
      const endpoint = state.sallerStatus.blocked
        ? `${process.env.NEXT_PUBLIC_BASE_URL}/auth/unban-account/${sallerInfo?.username}`
        : `${process.env.NEXT_PUBLIC_BASE_URL}/auth/ban-account/${sallerInfo?.username}`;

      // Simulate API call with static data
      await new Promise(resolve => setTimeout(resolve, 300));
      await fetchSallerInfo();

      setState(prev => ({
        ...prev,
        blockLoading: false,
        showBlockDialog: false
      }));

      toast.success(state.sallerStatus.blocked ? t("User Unblocked Successfully") : t("User Blocked Successfully"));
    } catch (error) {
      toast.error(error.message);
      setState(prev => ({ ...prev, blockLoading: false }));
    }
  }, [sallerInfo?.username, token, t, fetchSallerInfo, state.sallerStatus.blocked]);

  const handleReportSubmit = useCallback(async () => {
    const { reportDescription } = state;
    if (!reportDescription.trim()) {
      toast.error(t("Please provide a description"));
      return;
    }

    try {
      // Simulate API call with static data
      await new Promise(resolve => setTimeout(resolve, 300));

      await fetchSallerInfo();

      setState(prev => ({
        ...prev,
        reportDescription: '',
        showReportDialog: false
      }));

      toast.success(t("Report submitted successfully"));
    } catch (error) {
      toast.error(error.message);
    }
  }, [sallerInfo?.username, token, state.reportDescription, t, fetchSallerInfo]);

  const chatHandler = useCallback(() => {
    if (!token) {
      toast.error(t("Login Required"));
      return;
    }
    const existingRoom = state.rooms.some(
      (item) =>
        item.room_name.toLowerCase().includes(user?.username?.toLowerCase() || '') &&
        item.room_name.toLowerCase().includes(sallerInfo?.username?.toLowerCase() || '')
    );

    if (existingRoom) {
      router.push(`/Desktop/chat`);
    } else {
      setState(prev => ({ ...prev, chatModel: true }));
    }
  }, [state.rooms, user?.username, sallerInfo?.username, router, token, t]);

  useEffect(() => {
    fetchSallerInfo();
    fetchRooms();
  }, [fetchSallerInfo, fetchRooms]);

  useEffect(() => {
    const handleClickOutside = () => {
      setState(prev => ({ ...prev, isMenuOpen: false }));
    };

    if (state.isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [state.isMenuOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setState(prev => ({ ...prev, isMenuOpen: !prev.isMenuOpen }));
  };

  const handleSendMessage = () => {
    const message = state.chatMessage.trim()
    if (message) {
      sendMessage(message);
      setState(prev => ({ ...prev, chatMessage: "", chatModel: false }));
      router.push("/Mobile/oldChat")
    }
  }

  return (
    <div className="w-[50%] mt-10 mx-auto">
      <div className="bg-white rounded-2xl shadow-lg backdrop-blur-xl border border-gray-100">
        <div className="p-8">
          <div className="flex justify-between items-start">
            <div className="relative">
              <button
                onClick={toggleMenu}
                className="p-3 hover:bg-gray-50 rounded-xl transition-all duration-300 ease-in-out"
              >
                <MoreVertical className="w-6 h-6 text-gray-600" />
              </button>

              {state.isMenuOpen && (
                <div className={`absolute top-14 ${i18nextLng === "ar" ? "right-0" : "left-0"} w-64 bg-white rounded-xl shadow-2xl border border-gray-100 backdrop-blur-xl overflow-hidden z-50`}>
                  <div className="p-2">
                    <button
                      onClick={toggleFollowStatus}
                      disabled={state.followLoading}
                      className="w-full px-4 py-3 flex items-center gap-3 rounded-lg hover:bg-gray-50 transition-all duration-300 group"
                    >
                      {state.sallerStatus.followed ? (
                        <UserMinus className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
                      ) : (
                        <UserPlus className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
                      )}
                      <span className="text-gray-700 group-hover:text-gray-900">
                        {state.sallerStatus.followed ? t("Unfollow") : t("Follow")}
                      </span>
                    </button>
                    <button
                      onClick={() => setState(prev => ({ ...prev, showBlockDialog: true, isMenuOpen: false }))}
                      className="w-full px-4 py-3 flex items-center gap-3 rounded-lg hover:bg-red-50 transition-all duration-300 group"
                    >
                      <Shield className="w-5 h-5 text-red-600" />
                      <span className="text-red-600">
                        {state.sallerStatus.blocked ? t("Unblock") : t("Block")}
                      </span>
                    </button>
                    <button
                      onClick={() => setState(prev => ({ ...prev, showReportDialog: true, isMenuOpen: false }))}
                      className="w-full px-4 py-3 flex items-center gap-3 rounded-lg hover:bg-red-50 transition-all duration-300 group"
                    >
                      <Flag className="w-5 h-5 text-red-600" />
                      <span className="text-red-600">{t("Report")}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {sallerInfo?.username || t("N/A")}
                  </h2>
                  <BadgeCheck className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-sm font-medium">{sallerInfo?.followers_number || 0}</span>
                  <span className="text-sm">{t("Followers")}</span>
                </div>
              </div>
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform duration-300">
                <span className="text-3xl font-bold text-white">
                  {sallerInfo?.username?.charAt(0)?.toUpperCase() || t("A")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button
              onClick={chatHandler}
              className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-green-500 text-green-600 rounded-xl hover:bg-green-50 transition-all duration-300 group"
            >
              <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              <span className="font-medium">{t("Chat")}</span>
            </button>
            <Link
              href={`tel:`}
              className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 group shadow-lg"
            >
              <Phone className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              <span className="font-medium">{sallerInfo?.phone?.slice(1) || t("N/A")}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Modal Backdrops */}
      {(state.showBlockDialog || state.showReportDialog || state.chatModel) && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" />
      )}

      {/* Block Dialog */}
      {state.showBlockDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300">
            <h3 className="text-2xl font-semibold mb-4">
              {state?.sallerStatus?.blocked ? t("Unblock User") : t("Block User")}
            </h3>
            <p className="text-gray-600 mb-8">
              {state?.sallerStatus?.blocked
                ? t("Are you sure you want to unblock this user?")
                : t("Are you sure you want to block this user?")}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setState(prev => ({ ...prev, showBlockDialog: false }))}
                className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={toggleBlockStatus}
                disabled={state.blockLoading}
                className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-300 disabled:opacity-50 shadow-lg"
              >
                {t("Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Dialog */}
      {state.showReportDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300">
            <h3 className="text-2xl font-semibold mb-6">{t("Report User")}</h3>
            <textarea
              value={state.reportDescription}
              onChange={(e) => setState(prev => ({ ...prev, reportDescription: e.target.value }))}
              className="w-full p-4 border border-gray-200 rounded-xl mb-8 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              placeholder={t("Please describe the issue")}
              rows={4}
            />
            <div className="flex justify-end gap-4">
              <button
              onClick={() => setState(prev => ({ ...prev, showReportDialog: false, reportDescription: '' }))}
              className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={handleReportSubmit}
              className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-300 shadow-lg"
            >
              {t("Submit")}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Chat Dialog */}
    {state?.chatModel && token && (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300">
          <h3 className="text-2xl font-semibold mb-6">{t("Start a Conversation")}</h3>
          <textarea
            className="w-full p-4 border border-gray-200 rounded-xl mb-8 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            placeholder={t("Type your first message")}
            rows={4}
            onChange={(e) => setState(prev => ({ ...prev, chatMessage: e.target.value }))}
          />
          <div className="flex justify-end gap-4">
            <button
              onClick={() => setState(prev => ({ ...prev, chatModel: false }))}
              className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-300"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={handleSendMessage}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg"
            >
              {t("Send")}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default SellerSectionDesktop;